import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * Unified toast notifications.
 * Usage:
 *   const toast = useToast()
 *   toast.success('Salvato')
 *   toast.error('Errore connessione')
 *   toast.info('Sync in corso')
 *   toast.warn('Sessione scade fra 5 min')
 */

type ToastKind = 'ok' | 'error' | 'info' | 'warn'

type ToastItem = {
  id: number
  kind: ToastKind
  message: string
}

type ToastContextValue = {
  push: (kind: ToastKind, message: string, durationMs?: number) => void
  success: (message: string, durationMs?: number) => void
  error: (message: string, durationMs?: number) => void
  info: (message: string, durationMs?: number) => void
  warn: (message: string, durationMs?: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])

  const push = useCallback(
    (kind: ToastKind, message: string, durationMs = 4000) => {
      const id = nextId++
      setItems((prev) => [...prev, { id, kind, message }])
      setTimeout(() => {
        setItems((prev) => prev.filter((t) => t.id !== id))
      }, durationMs)
    },
    [],
  )

  const value: ToastContextValue = {
    push,
    success: (m, d) => push('ok', m, d),
    error: (m, d) => push('error', m, d),
    info: (m, d) => push('info', m, d),
    warn: (m, d) => push('warn', m, d),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport items={items} onDismiss={(id) => setItems((prev) => prev.filter((t) => t.id !== id))} />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Fallback if provider not mounted (e.g., login route renders without shell).
    return {
      push: (_k, m) => console.log('[toast]', m),
      success: (m) => console.log('[toast:ok]', m),
      error: (m) => console.error('[toast:error]', m),
      info: (m) => console.log('[toast:info]', m),
      warn: (m) => console.warn('[toast:warn]', m),
    }
  }
  return ctx
}

function ToastViewport({
  items,
  onDismiss,
}: {
  items: ToastItem[]
  onDismiss: (id: number) => void
}) {
  if (items.length === 0) return null
  return (
    <div className="toast-viewport" role="region" aria-label="Notifiche" aria-live="polite">
      {items.map((t) => (
        <ToastRow key={t.id} item={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  )
}

function ToastRow({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const [entering, setEntering] = useState(true)
  useEffect(() => {
    const t = setTimeout(() => setEntering(false), 10)
    return () => clearTimeout(t)
  }, [])
  return (
    <div className={`toast toast-${item.kind}${entering ? ' toast-enter' : ''}`} role="status">
      <span className="toast-icon" aria-hidden="true">
        {item.kind === 'ok' && '✓'}
        {item.kind === 'error' && '✕'}
        {item.kind === 'info' && 'ⓘ'}
        {item.kind === 'warn' && '⚠'}
      </span>
      <span className="toast-message">{item.message}</span>
      <button
        type="button"
        className="toast-close"
        onClick={onDismiss}
        aria-label="Chiudi notifica"
      >
        ×
      </button>
    </div>
  )
}
